import { useState, useContext, useEffect } from "react";
import api, { showAlert, AuthContext } from "../util/api";
import { useNavigate } from "react-router-dom";
import Navbar from "../header";
import Footer from "../footer";

export default function PatientForm() {
  const route = useNavigate();
  const { User, Patient, patientFunction } = useContext(AuthContext);

  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [previewImage, setPreviewImage] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    gender: "",
    bio: "",
    profilePicture: null,
  });

  // -------------------------
  // Handle Input Changes
  // -------------------------
  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "profilePicture" && files?.[0]) {
      const file = files[0];
      setFormData({ ...formData, profilePicture: file });

      const reader = new FileReader();
      reader.onload = () => setPreviewImage(reader.result);
      reader.readAsDataURL(file);
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  // -------------------------
  // Submit Form
  // -------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    setSubmitting(true);
    setProgress(20);

    const formDataToSend = new FormData();
    Object.keys(formData).forEach((key) =>
      formDataToSend.append(key, formData[key])
    );

    formDataToSend.append("user_id", User.id);

    try {
      const response = await api.post(
        "/patient/register",
        formDataToSend,
        {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => {
            if (evt.total) {
              setProgress(
                Math.round((evt.loaded / evt.total) * 100)
              );
            }
          },
        }
      );

      const data = response.data;

      if (data.status === "success") {
        patientFunction(data.patient, data.user);
        showAlert("success", "Success", data.message);
        route("/patient-dashboard");
      } else {
        showAlert("error", "Error", data.message);
      }
    } catch {
      showAlert("error", "Error", "Submission failed.");
    } finally {
      setSubmitting(false);
      setTimeout(() => setProgress(0), 1500);
    }
  };

  // -------------------------
  // Load Existing Patient Data
  // -------------------------
  useEffect(() => {
    if (!Patient) return;

    setFormData({
      name: Patient.name || "",
      phone: Patient.phone || "",
      gender: Patient.gender || "",
      bio: Patient.bio || "",
      profilePicture: null,
    });

    setPreviewImage(Patient.profilePicture);
  }, [Patient]);

  return (
    <>
      <Navbar menuColor={{ color: "#020617" }} />

      <style>{`
        .glass {
          background: rgba(255,255,255,0.7);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255,255,255,0.4);
        }
      `}</style>

      <main
        style={{
          minHeight: "100vh",
          paddingTop: "120px",
          paddingBottom: "80px",
          background:
            "radial-gradient(circle at top, #e0f2fe, #f8fafc 45%, #eef2ff)",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div style={{ width: "100%", maxWidth: "520px", padding: "20px" }}>
          <div
            className="glass"
            style={{
              borderRadius: "26px",
              padding: "35px",
              boxShadow: "0 25px 60px rgba(0,0,0,0.15)",
            }}
          >
            {/* Header */}
            <h2
              style={{
                fontSize: "26px",
                fontWeight: 900,
                color: "#020617",
                textAlign: "center",
              }}
            >
              Patient Profile
            </h2>

            <p
              style={{
                textAlign: "center",
                color: "#475569",
                marginBottom: "25px",
              }}
            >
              Complete your profile to continue
            </p>

            {/* Avatar */}
            <div style={{ textAlign: "center", marginBottom: "25px" }}>
              <div
                style={{
                  width: "120px",
                  height: "120px",
                  margin: "0 auto",
                  position: "relative",
                }}
              >
                <img
                  src={previewImage || "/logo_back_remove.png"}
                  alt="Profile"
                  style={{
                    width: "120px",
                    height: "120px",
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "4px solid white",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
                  }}
                />

                <label
                  htmlFor="profilePicture"
                  style={{
                    position: "absolute",
                    bottom: "0",
                    right: "0",
                    background:
                      "linear-gradient(to right, #6366f1, #8b5cf6)",
                    color: "white",
                    borderRadius: "50%",
                    padding: "6px 8px",
                    cursor: "pointer",
                    fontSize: "14px",
                  }}
                >
                  📷
                </label>

                <input
                  type="file"
                  id="profilePicture"
                  name="profilePicture"
                  accept="image/*"
                  onChange={handleChange}
                  hidden
                />
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <input
                value={User.email}
                readOnly
                style={inputStyle}
              />

              <input
                required
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Full Name"
                style={inputStyle}
              />

              <input
                required
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Phone Number"
                style={inputStyle}
              />

              <select
                required
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                style={inputStyle}
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>

              <textarea
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                placeholder="About yourself (optional)"
                rows={4}
                style={{ ...inputStyle, resize: "none" }}
              />

              {/* Progress */}
              {progress > 0 && (
                <div
                  style={{
                    height: "6px",
                    background: "#e5e7eb",
                    borderRadius: "10px",
                    marginBottom: "15px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${progress}%`,
                      height: "100%",
                      background:
                        "linear-gradient(to right, #6366f1, #8b5cf6)",
                      transition: "width 0.4s ease",
                    }}
                  />
                </div>
              )}

              <button
                disabled={submitting}
                style={{
                  width: "100%",
                  padding: "14px",
                  borderRadius: "14px",
                  background:
                    "linear-gradient(to right, #6366f1, #8b5cf6)",
                  color: "white",
                  fontWeight: 700,
                  border: "none",
                  cursor: submitting ? "not-allowed" : "pointer",
                  boxShadow:
                    "0 15px 35px rgba(99,102,241,0.4)",
                }}
              >
                {submitting ? "Submitting..." : "Submit Profile"}
              </button>
            </form>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}

const inputStyle = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: "12px",
  border: "1px solid #cbd5e1",
  marginBottom: "15px",
  fontSize: "14px",
  outline: "none",
};
